import React from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { ApolloError } from '@apollo/client';

import { Button } from '@oysterjs/ui/button';
import {
  LoggedInAgencyUserFieldsFragment,
  SubscriptionPeriod,
  SubscriptionPlan,
  UpdateSubscriptionDocument,
  UpdateSubscriptionMutation,
  UpdateSubscriptionMutationVariables
} from '~/api/graphql';
import { client } from '~/api/client';

export const RenewalMigrationPaymentForm = (props: {
  agencyUser: LoggedInAgencyUserFieldsFragment;
  plan: SubscriptionPlan;
  period: SubscriptionPeriod;
  seats: number;
  voiceAgents: number;
  onSuccess: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setIsProcessing(true);
    setMessage(null);
    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required'
      });
      if (result.error) {
        setMessage(result.error.message || 'Unable to save payment method.');
        return;
      }
      const updateResult = await client.mutate<
        UpdateSubscriptionMutation,
        UpdateSubscriptionMutationVariables
      >({
        mutation: UpdateSubscriptionDocument,
        variables: {
          input: {
            plan: props.plan,
            period: props.period,
            seats: props.seats,
            voiceAgents: props.voiceAgents
          }
        }
      });
      if (updateResult.errors?.length) {
        throw new ApolloError({ graphQLErrors: updateResult.errors });
      }
      if (updateResult.data?.updateSubscription?.plan) {
        props.onSuccess();
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to confirm renewal.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <PaymentElement
        options={{
          layout: { type: 'tabs' },
          business: { name: props.agencyUser.agency.name }
        }}
      />
      {message && <p className="m-0 text-xs text-red-600 dark:text-red-400">{message}</p>}
      <Button color="sky" type="submit" loading={isProcessing} disabled={!stripe || !elements}>
        Confirm renewal
      </Button>
    </form>
  );
};
